// Google Analytics Event Tracking Utilities

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

// Track custom events
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
  }
};

// Track form submissions
export const trackFormSubmission = (formName: string) => {
  trackEvent('generate_lead', {
    event_category: 'engagement',
    event_label: formName,
  });
};

// Track resource downloads
export const trackDownload = (resourceName: string) => {
  trackEvent('download', {
    event_category: 'engagement',
    event_label: resourceName,
  });
};

// Track CTA clicks
export const trackCTAClick = (ctaName: string, location: string) => {
  trackEvent('click', {
    event_category: 'cta',
    event_label: ctaName,
    event_location: location,
  });
};

// Track page views (for SPA navigation)
export const trackPageView = (pageName: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-S5ZQQ7PXRN', {
      page_path: `/${pageName}`,
      page_title: pageName,
    });
  }
};

// Track assessment starts
export const trackAssessmentStart = () => {
  trackEvent('begin_assessment', {
    event_category: 'engagement',
    event_label: 'iam_assessment',
  });
};

// Track outbound links
export const trackOutboundLink = (url: string) => {
  trackEvent('click', {
    event_category: 'outbound',
    event_label: url,
  });
};

// A/B Testing Analytics

/**
 * Set the homepage variant as a GA4 user property.
 * This allows segmenting all analytics by variant.
 */
export const setABTestVariant = (variant: 'control' | 'simplified') => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Set as user property for all future events
    window.gtag('set', 'user_properties', {
      homepage_variant: variant,
    });
    // Also fire an event for variant assignment
    trackEvent('ab_variant_assigned', {
      event_category: 'ab_test',
      event_label: 'homepage_simplified_2025',
      variant: variant,
    });
  }
};

/**
 * Track demo booking conversion with variant info.
 */
export const trackDemoBooking = (variant: 'control' | 'simplified', source: string) => {
  trackEvent('demo_booking', {
    event_category: 'conversion',
    event_label: source,
    homepage_variant: variant,
  });
};

// Chat Widget Analytics

/**
 * Track when chat widget is opened.
 */
export const trackChatOpened = (variant: 'control' | 'simplified') => {
  trackEvent('chat_opened', {
    event_category: 'chat_widget',
    homepage_variant: variant,
  });
};

/**
 * Track chat message sent by user.
 */
export const trackChatMessageSent = (sessionId: string, messageNumber: number) => {
  trackEvent('chat_message_sent', {
    event_category: 'chat_widget',
    session_id: sessionId,
    message_number: messageNumber,
  });
};

/**
 * Track when user clicks suggested prompt.
 */
export const trackChatPromptClicked = (prompt: string) => {
  trackEvent('chat_prompt_clicked', {
    event_category: 'chat_widget',
    event_label: prompt,
  });
};

/**
 * Track when chat leads to demo request.
 */
export const trackChatDemoRequest = (sessionId: string) => {
  trackEvent('chat_demo_request', {
    event_category: 'conversion',
    event_label: 'from_chat',
    session_id: sessionId,
  });
};

/**
 * Track chat message feedback (thumbs up/down).
 */
export const trackChatFeedback = (sessionId: string, messageIndex: number, feedback: 'up' | 'down') => {
  trackEvent('chat_feedback', {
    event_category: 'chat_widget',
    event_label: feedback,
    session_id: sessionId,
    message_index: messageIndex,
  });
};

/**
 * Track when user clicks an engagement prompt.
 */
export const trackEngagementPromptClicked = (sessionId: string, promptText: string) => {
  trackEvent('engagement_prompt_clicked', {
    event_category: 'chat_widget',
    event_label: promptText,
    session_id: sessionId,
  });
};